import { HttpClient } from '@angular/common/http';
import { EventEmitter, Injectable } from '@angular/core';
import { ModuleAuthEnum } from '@enums/module-auth.enum';
import { environment } from '@environments/environment';
import { ClientBrowserHelperService } from '@helpers/client-browser-helper.service';
import { AuthSettingsI, RefreshTokenDataI, SignOutDataI } from '@interfaces/auth.interface';
import { CognitoUserSessionDataI } from '@interfaces/cognito-user-data.interface';
import { CognitoCodeDeliveryDetails } from '@interfaces/cognito/CognitoCodeDeliveryDetails';
import { UserLoginDataI } from '@interfaces/user-login-data.interface';
import { GlobalStoreService } from '@modules/global/store/global-store.service';
import { firstValueFrom, Observable, BehaviorSubject } from '@node_modules/rxjs';
import { NgxSpinnerService } from 'ngx-spinner';
import { CookieService } from 'ngx-cookie-service';
import * as cookieConst from '@constants/cookies-auth.const';
import { LocalStorageService } from '@services/common/local-storage.service';
import { UserHelperService } from '@helpers/user-helper.service';
import { ReportPauseService } from '@modules/reports/report-pause/report-pause.service';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private baseUrl = environment.OMNISMART_API_USERS;
  private slug_url = 'authentication';
  private slug_url_invalidate_auth = 'invalidate-auth';
  private slug_url_signout = 'signout';
  private readonly SIGNOUT_REASON_KEY = 'omni-last-signout';
  public submitEvent = new EventEmitter<any>();
  public submitIdentityProviderEvent = new EventEmitter<{ code: string; identityId: string }>();
  public forgotPasswordEvent = new EventEmitter<any>();
  public confirmMFACode = new EventEmitter<string>();
  constructor(
    private readonly http: HttpClient,
    private clientBrowserHelperService: ClientBrowserHelperService,
    private globalStore: GlobalStoreService,
    private spinner: NgxSpinnerService,
    private cookieService: CookieService,
    private localStorageService: LocalStorageService,
    private userHelperService: UserHelperService,
    private reportPauseService: ReportPauseService
  ) {}

  public signin(email: string, password: string, userLoginData: UserLoginDataI): Observable<CognitoUserSessionDataI> {
    try {
      return this.signinOmnismartApi({ email, password, userLoginData });
    } catch (error) {
      throw new Error('AuthenticationService signin error: ' + error);
    }
  }

  public signinIdentityProvider(
    identityId: string,
    code: string,
    userLoginData: UserLoginDataI
  ): Observable<CognitoUserSessionDataI> {
    try {
      return this.http.post<CognitoUserSessionDataI>(
        `${this.baseUrl}${this.slug_url}/login-identity-provider/${identityId}/${code}`,
        { userLoginData }
      );
    } catch (error) {
      throw new Error('AuthenticationService signinIdentityProvider error: ' + error);
    }
  }

  public signinOther(userId: string, userIdOther: string): Observable<any> {
    try {
      return this.signinOmnismartApiOther({ userId, userIdOther });
    } catch (error) {
      throw new Error('AuthenticationService signin other error: ' + error);
    }
  }

  public signinOtherByCustomer(customerIdOther: string): Observable<any> {
    try {
      return this.signinOmnismartApiOtherByCustomer({ customerIdOther });
    } catch (error) {
      throw new Error('AuthenticationService signin other error: ' + error);
    }
  }

  public signoutOther(data: SignOutDataI): Observable<CognitoUserSessionDataI> {
    try {
      return this.signoutOmnismartApiOther(data);
    } catch (error) {
      throw new Error('AuthenticationService signout other error: ' + error);
    }
  }
  public getSettings(): Observable<AuthSettingsI> {
    return this.http.get<AuthSettingsI>(`${this.baseUrl}${this.slug_url}/settings`);
  }

  public postSettings(body: AuthSettingsI): Observable<AuthSettingsI> {
    return this.http.post<AuthSettingsI>(`${this.baseUrl}${this.slug_url}/settings`, body);
  }
  public patchSettings(body: AuthSettingsI): Observable<AuthSettingsI> {
    return this.http.patch<AuthSettingsI>(`${this.baseUrl}${this.slug_url}/settings`, body);
  }

  private signinOmnismartApi(data: {
    email: string;
    password: string;
    userLoginData: UserLoginDataI;
  }): Observable<CognitoUserSessionDataI> {
    try {
      return this.http.post<CognitoUserSessionDataI>(`${this.baseUrl}${this.slug_url}/login`, data);
    } catch (error) {
      throw new Error('AuthenticationService signin error: ' + error);
    }
  }

  public sendMFACode(
    code: string,
    userName: string,
    password: string,
    userLoginData: UserLoginDataI
  ): Observable<CognitoUserSessionDataI> {
    return this.http.post<CognitoUserSessionDataI>(`${this.baseUrl}${this.slug_url}/mfa-send-code`, {
      code,
      userName,
      userLoginData,
      password,
    });
  }

  public mfaVerify(
    code: string,
    token: string,
    password?: string,
    userLoginData?: UserLoginDataI
  ): Observable<{ success: boolean }> {
    return this.http.post<any>(
      `${this.baseUrl}${this.slug_url}/mfa-verify`,
      {
        code,
        password,
        userLoginData,
      },
      {
        headers: {
          Authorization: `bearer ${token}`,
        },
      }
    );
  }
  public mfaByEmailVerify(
    body: {
      code: string;
      password?: string;
      userLoginData: UserLoginDataI;
    },
    token: string
  ): Observable<{ success: boolean; updated: boolean }> {
    return this.http.post<any>(`${this.baseUrl}${this.slug_url}/mfa-by-email-verify`, body, {
      headers: {
        Authorization: `bearer ${token}`,
      },
    });
  }

  public mfaByEmailSendEmail(email: string, session?: string): Observable<any> {
    return this.http.post<CognitoUserSessionDataI>(`${this.baseUrl}${this.slug_url}/mfa-by-email-send-email`, {
      email,
      session,
    });
  }

  public mfaSendCodeByEmail(body: {
    code: string;
    email: string;
    password: string;
    session: string;
    userLoginData: UserLoginDataI;
  }): Observable<CognitoUserSessionDataI> {
    return this.http.post<CognitoUserSessionDataI>(`${this.baseUrl}${this.slug_url}/mfa-by-email-send-code`, body);
  }

  public mfaAssociate(token: string): Observable<{ qrCode: string; secret: string }> {
    return this.http.post<any>(`${this.baseUrl}${this.slug_url}/mfa-associate`, null, {
      headers: {
        Authorization: `bearer ${token}`,
      },
    });
  }

  private signinOmnismartApiOther(data: { userId: string; userIdOther: string }): Observable<any> {
    try {
      return this.http.post<any>(`${this.baseUrl}${this.slug_url}/login-other`, data);
    } catch (error) {
      throw new Error('AuthenticationService signin error: ' + error);
    }
  }

  private signoutOmnismartApiOther(data: SignOutDataI): Observable<CognitoUserSessionDataI> {
    try {
      return this.http.post<CognitoUserSessionDataI>(`${this.baseUrl}${this.slug_url}/signout-other`, data);
    } catch (error) {
      throw new Error('AuthenticationService signin error: ' + error);
    }
  }

  private signinOmnismartApiOtherByCustomer(data: { customerIdOther: string }): Observable<any> {
    try {
      return this.http.post<any>(`${this.baseUrl}${this.slug_url}/login-other-by-customer`, data);
    } catch (error) {
      throw new Error('AuthenticationService signin error: ' + error);
    }
  }

  public generateCrossLoginToken(targetPlatform: string): Observable<{ token: string; redirectUrl: string }> {
    return this.http.post<{ token: string; redirectUrl: string }>(`${this.baseUrl}${this.slug_url}/cross-login-token`, {
      targetPlatform,
    });
  }

  public exchangeCrossLoginToken(token: string): Observable<CognitoUserSessionDataI & { isNewUser?: boolean }> {
    return this.http.post<CognitoUserSessionDataI & { isNewUser?: boolean }>(
      `${this.baseUrl}${this.slug_url}/exchange-cross-login-token`,
      { token }
    );
  }

  public refreshToken(options: RefreshTokenDataI): Observable<CognitoUserSessionDataI> {
    try {
      return this.http.post<CognitoUserSessionDataI>(`${this.baseUrl}${this.slug_url}/refresh-token`, options);
    } catch (error) {
      throw new Error('AuthenticationService refreshToken error: ' + error);
    }
  }

  public async signout(userId?: string) {
    this.globalStore.setLogout(true);
    const device = this.clientBrowserHelperService.getDevice();
    // Best-effort server-side signout: a network/server failure (e.g. right after a
    // wake from sleep) must NOT abort the local teardown below, otherwise the session
    // is left half-signed-out (cookies deleted by the interceptor, localStorage kept,
    // no redirect). The local cleanup + reload always run.
    try {
      // end the agent pause while the auth token is still available — after
      // localStorage.clear()/cookie delete below the interceptor can't authenticate the request.
      await this.finishPauseOnLogout();
      if (userId) {
        await firstValueFrom(
          this.http.post<void>(`${this.baseUrl}${this.slug_url}/${this.slug_url_signout}`, {
            device,
          })
        );
      }
    } catch (error) {
      // swallow — the teardown + redirect below must complete regardless.
    }

    this.spinner.hide();

    const dashboardLayout = localStorage.getItem('dashboard_layout');
    this.localStorageService.clear();
    if (dashboardLayout) {
      localStorage.setItem('dashboard_layout', dashboardLayout);
    }
    this.cookieService.delete('idToken', cookieConst.path, cookieConst.domain, true, cookieConst.sameSite);
    this.cookieService.delete('refreshToken', cookieConst.path, cookieConst.domain, true, cookieConst.sameSite);
    window.location.reload();
  }

  private async finishPauseOnLogout(): Promise<void> {
    const loggedInUser = this.userHelperService.getUserAttributes();
    if (!loggedInUser) {
      return;
    }
    try {
      const userId = loggedInUser['custom:userId'];
      const customer = loggedInUser['custom:customer'];
      await firstValueFrom(this.reportPauseService.endPauseByUserId(userId, customer));
    } catch (error) {
      // tolerant: logout must proceed even if end-pause fails (mirrors finishPauses on login)
      console.error('finishPauseOnLogout error:', error);
    }
  }

  // Records why a session is ending. A silent front-side logout never hits the backend signout
  // and the reload wipes the console, so without this it leaves no trace anywhere. Written to a
  // KEEP_PREFIXES key so it survives the clear(), and overwritten in place — a logout/relogin
  // loop coalesces to the last reason instead of flooding. Reported once from the login page.
  public stashSignoutReason(reason: string): void {
    try {
      const attrs: any = this.userHelperService.getUserAttributes() ?? {};
      localStorage.setItem(
        this.SIGNOUT_REASON_KEY,
        JSON.stringify({
          reason,
          userId: attrs['custom:userId'] ?? '',
          customer: attrs['custom:customer'] ?? '',
          browserId: this.clientBrowserHelperService.getBrowserId(),
          url: window.location?.pathname ?? '',
          ts: Date.now(),
        })
      );
    } catch {
      // best-effort telemetry — never block the logout
    }
  }

  // Called from the login page (post-reload): if the previous session stashed a reason, send it
  // once to the anonymous collector and drop the key. The login page is a normal context, so a
  // plain fire-and-forget POST is enough — no sendBeacon/unload fragility, and it runs after the
  // churn (one report per real logout, not per signout).
  public reportStashedSignout(): void {
    try {
      const raw = localStorage.getItem(this.SIGNOUT_REASON_KEY);
      if (!raw) {
        return;
      }
      localStorage.removeItem(this.SIGNOUT_REASON_KEY);
      const payload = { ...JSON.parse(raw), module: 'OMNISMART' };
      this.http.post(`${this.baseUrl}${this.slug_url}/signout-report`, payload).subscribe({ error: () => {} });
    } catch {
      // ignore — telemetry must never break the login screen
    }
  }

  public forgotPassword(email: string): Promise<CognitoCodeDeliveryDetails> {
    return firstValueFrom(this.http.post<any>(`${this.baseUrl}${this.slug_url}/forgot-password`, { email }));
  }

  public confirmNewPassword(email: string, password: string, code: string) {
    return firstValueFrom(
      this.http.post<any>(`${this.baseUrl}${this.slug_url}/forgot-password-confirmation`, { email, password, code })
    );
  }

  public invalidateAuth(options: { _id: string; invalidateAll?: boolean }): Observable<void> {
    const { _id, invalidateAll } = options;
    try {
      return this.http.post<void>(`${this.baseUrl}${this.slug_url}/${this.slug_url_invalidate_auth}/${_id}`, {
        invalidateAll,
        module: ModuleAuthEnum.OMNISMART,
      });
    } catch (error) {
      throw new Error('AuthenticationService invalidateAuth error: ' + error);
    }
  }

  getDataAuth(options: any) {
    return this.http.post(`${this.baseUrl}${this.slug_url}/login-with-auth`, {
      ...options,
      module: ModuleAuthEnum.OMNISMART,
    });
  }
}
